import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import PrescriptionSubGroup from './components/prescriptionSubGroup';
import MedicationInput from './components/medicationInput';
import MedicationNoteInput from './components/medicationNoteInput';
import _ from 'lodash';
import moment from 'moment';

const COURSE_OF_THERAPY_TYPE_OPTIONS = [
    {
        id: 'acute',
        name: 'Short course (acute) therapy',
    },
    {
        id: 'continuous',
        name: 'Continuous long term therapy',
    },
    // {
    //     id: 'seasonal',
    //     name: 'Seasonal',
    // },
];

// ids: [inpatient|outpatient|community|discharge]
// names: [Inpatient|Outpatient|Community|Discharge]
// helps: [Includes requests for medications to be administered or consumed in an inpatient or acute care setting|Includes requests for medications to be administered or consumed in an outpatient setting (for example, Emergency Department, Outpatient Clinic, Outpatient Surgery, Doctor's office)|Includes requests for medications to be administered or consumed by the patient in their home (this would include long term care or nursing homes, hospices, etc.)|Includes requests for medications created when the patient is being released from a facility]
const CATEGORY_OPTIONS = [
    {
        id: 'inpatient',
        name: 'Inpatient',
        help: 'Includes requests for medications to be administered or consumed in an inpatient or acute care setting',
    },
    {
        id: 'outpatient',
        name: 'Outpatient',
        help: "Includes requests for medications to be administered or consumed in an outpatient setting (for example, Emergency Department, Outpatient Clinic, Outpatient Surgery, Doctor's office)",
    },
    {
        id: 'community',
        name: 'Community',
        help: 'Includes requests for medications to be administered or consumed by the patient in their home (this would include long term care or nursing homes, hospices, etc.)',
    },
    {
        id: 'discharge',
        name: 'Discharge',
        help: 'Includes requests for medications created when the patient is being released from a facility',
    },
];

const PrescriptionMainData = forwardRef(({ form, handleChange, disabled }, ref) => {
    const { t } = useTranslation();

    const [error, setError] = useState(false);

    // Expose imperative API to parent components
    useImperativeHandle(
        ref,
        () => ({
            validateForm: () => valid(),
        }),
        [form],
    );

    const onInputChange = ({ field, value }) => {
        // Prevent changes if form is disabled
        if (disabled) return;

        handleChange((prev) => ({
            ...prev,
            [field]: {
                ...prev[field],
                ...value,
            },
        }));
    };

    const validateValidityDates = () => {
        return (
            (_.isNil(form.validityStartDate.value) && _.isNil(form.validityEndDate.value)) ||
            (!!form.validityStartDate.value &&
                !!form.validityEndDate.value &&
                moment(form.validityStartDate.value).isSameOrBefore(form.validityEndDate.value) &&
                moment(form.validityStartDate.value).isSameOrBefore(moment(), 'day'))
        );
    };

    const validValidityDates = useMemo(() => {
        return validateValidityDates();
    }, [form.validityStartDate, form.validityEndDate]);

    const valid = () => {
        const isValid = validValidityDates && !_.isEmpty(form.courseOfTherapyType.value) && !_.isEmpty(form.category.value);
        setError(!isValid);
        return isValid;
    };

    return (
        <>
            <Stack spacing={2}>
                <PrescriptionSubGroup title={t('Prescription Main Data')}>
                    <MedicationInput
                        title="Valid From"
                        type="date"
                        field="validityStartDate"
                        disabled={disabled}
                        value={form.validityStartDate}
                        onChange={onInputChange}
                        hideComment
                        error={error && !validateValidityDates()}
                    />
                    <MedicationInput
                        title="Valid To"
                        type="date"
                        field="validityEndDate"
                        disabled={disabled}
                        value={form.validityEndDate}
                        onChange={onInputChange}
                        hideComment
                        error={error && !validateValidityDates()}
                    />
                    <MedicationInput
                        title={t('Course of Therapy')}
                        type="select"
                        field="courseOfTherapyType"
                        disabled={disabled}
                        value={form.courseOfTherapyType}
                        onChange={onInputChange}
                        error={error && _.isEmpty(form.courseOfTherapyType.value)}
                        hideComment
                        options={COURSE_OF_THERAPY_TYPE_OPTIONS}
                        clearButtonVisible={false}
                    />
                    <MedicationInput
                        title={t('Category')}
                        type="select"
                        field="categoty"
                        disabled={disabled}
                        value={form.category}
                        onChange={onInputChange}
                        error={error && _.isEmpty(form.category.value)}
                        hideComment
                        options={CATEGORY_OPTIONS}
                        clearButtonVisible={false}
                    />
                    <MedicationNoteInput
                        title="Note"
                        type="prescription"
                        disabled={disabled}
                        value={form.instructions?.value}
                        onChange={(note) => onInputChange({ field: 'instructions', value: note })}
                    />
                </PrescriptionSubGroup>
            </Stack>
        </>
    );
});

export default PrescriptionMainData;
